import React, {useState} from 'react';
import _ from 'underscore';
import {useThrottledCallback} from 'use-debounce';
import {
	Tooltip as MuiTooltip,
	type TooltipProps as MuiTooltipProps,
} from '@mui/material';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useUpdateEffect} from '@netx/core-components/hooks/useUpdateEffect';
import {useWindowEvent} from '@netx/core-components/hooks/useWindowEvent';
import {splitEventHandler} from '@netx/core-next/utils';

export interface TooltipProps extends MuiTooltipProps {
	disabled?: boolean;
	onTooltipToggle?: (open: boolean) => void;
}

/**
 * Wrap mui `Tooltip` to support dismissing on scroll as well as a `disabled` prop
 */
export const Tooltip = ({
	disabled,
	children,
	title,
	open: _open,
	onClose,
	onOpen,
	onTooltipToggle,
	TransitionProps,
	...props
}: TooltipProps) => {
	const [open, setOpen] = useState(_open || false);

	const toggleOpen = useConstCallback(
		(open: boolean) => () => setOpen(disabled ? false : open),
	);

	const scrollListener = useThrottledCallback(() => {
		setOpen(false);
	}, 25);

	useWindowEvent('scroll', scrollListener, undefined, !open);

	useUpdateEffect(() => {
		_open != null && setOpen(_open);
	}, [_open]);

	useUpdateEffect(() => {
		disabled && setOpen(false);
	}, [disabled]);

	return (
		<MuiTooltip
			disableInteractive
			title={disabled ? '' : title}
			{...props}
			open={open}
			onOpen={splitEventHandler(onOpen, toggleOpen(true))}
			onClose={splitEventHandler(onClose, toggleOpen(false))}
			PopperProps={{
				keepMounted: false,
				...props.PopperProps,
				modifiers: [
					{
						name: 'topLogger',
						enabled: true,
						phase: 'beforeRead',
						fn: ({state}) => {
							// console.log({...state.rects});
							if (
								!state.rects.reference.height &&
								!state.rects.reference.width
							) {
								state.styles.popper.opacity = '0';
							} else {
								state.styles.popper.opacity = '1';
							}
						},
					},
					...(props.PopperProps?.modifiers ?? []),
				],
			}}
			TransitionProps={{
				...TransitionProps,
				// TODO: when we set these it overrides somehting that mu i is not taking in to account
				// then the tooltip stays in the dom forever (well, until the component using it is unmounted)
				// we do need this for a gross hack in our value chip component... need to find a better solution there etiher way
				...(onTooltipToggle && {
					onEntering: splitEventHandler(TransitionProps?.onEntering, () =>
						onTooltipToggle?.(true),
					),
					onExited: splitEventHandler(TransitionProps?.onExited, () =>
						onTooltipToggle?.(false),
					),
				}),
			}}
		>
			{children}
		</MuiTooltip>
	);
};

export default Tooltip;
